/* eslint-disable */
import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Autocomplete,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  LocationOn as LocationOnIcon,
  QueryBuilder as QueryBuilderIcon,
  LocalShipping as LocalShippingIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import { OrderProps } from "store/modules/Orders/types.store";
import { addMinutes, format } from "date-fns";

import { formatByText } from "utils";

import { handleAddDriveOrderRequest } from "store/modules/Orders/thunk.store";
import { useAppDispatch } from "store/hooks";
import { getDealerDrivers } from "services/logistics/getDealerDrivers";
import {
  DealerDriverDto,
  isOnlineDriverStatus,
} from "types/dealerDrivers";
import { getOrderDeliveryPersonDisplayName } from "utils/coverageAreaDriverRules";
import * as Styled from "./styles";

interface CardActionsProps {
  data?: never | OrderProps;
}

const CardActions: React.FC<CardActionsProps> = ({ data }) => {
  const { t } = useTranslation();
  const [deliveryTime, setDeliveryTime] = useState("");
  const [allDrivers, setAllDrivers] = useState<DealerDriverDto[]>();
  const [driveSelected, setDriveSelected] = useState<{
    label: string;
    id: string;
  }>();
  const theme = useTheme();
  const media = useMediaQuery(theme.breakpoints.up(1328));

  const [addDrive, setAddDrive] = useState<boolean>(false);

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (data?.createdAt && data?.delivery?.eta?.current) {
      const timeCreateAt = addMinutes(
        new Date(data?.createdAt),
        data?.delivery?.eta?.current,
      );
      setDeliveryTime(format(timeCreateAt, "HH:mm:ss"));
    }
  }, [data]);

  const IUsers = useMemo(() => {
    const drivers = allDrivers?.filter(
      (driver) => driver.enabled && isOnlineDriverStatus(driver.status),
    );
    return drivers?.map((driver) => ({
      label: (driver.name ?? "").trim(),
      id: driver.id,
    }));
  }, [allDrivers]);

  const handleAddDrive = useCallback(async () => {
    if (addDrive) {
      await dispatch(
        handleAddDriveOrderRequest({
          dealerId: data?.dealer?.id || "",
          orderId: data?.id || "",
          driverName: driveSelected?.label || "",
          driverId: driveSelected?.id || "",
        }),
      ).unwrap;

      setAddDrive(false);
    }
  }, [
    data?.dealer?.id,
    data?.id,
    driveSelected?.id,
    dispatch,
    driveSelected?.label,
    addDrive,
  ]);

  const handledUsers = async () => {
    const dealerId = data?.dealer.id || "";
    if (!dealerId) {
      return;
    }
    const response = await getDealerDrivers(dealerId, {
      statuses: ["online"],
    });
    setAllDrivers(response.data);
  };

  // TODO:  remover mocks
  return (
    <Styled.Container>
      <Grid container direction="row" rowGap={2}>
        <Grid item sm={12} md={6} xs={6} xl={6}>
          <Grid container rowGap={2}>
            <Grid item sm={12} md={6} xs={6} xl={6}>
              <Styled.Grid>
                <LocationOnIcon fontSize="small" color="inherit" />
                <Styled.ContainerText>
                  <Typography variant="caption" textAlign="start">
                    {t("deliveryAddress")}
                  </Typography>
                  <Typography variant="subtitle2" textAlign="start">
                    {data?.delivery?.address?.street}
                  </Typography>
                  <Typography variant="caption" textAlign="start">
                    {data?.delivery?.address?.city}
                  </Typography>
                </Styled.ContainerText>
              </Styled.Grid>
            </Grid>
            <Grid item sm={12} md={6} xs={6} xl={6}>
              <Styled.Grid>
                <QueryBuilderIcon fontSize="small" color="inherit" />
                <Styled.ContainerText>
                  <Typography variant="caption" textAlign="start">
                    {t("deliverBy")}
                  </Typography>
                  <Typography variant="subtitle2" textAlign="start">
                    {deliveryTime
                      ? `${deliveryTime} (${data?.delivery?.eta?.current} ${t(
                          "minutes",
                        )})`
                      : t("undefined")}
                  </Typography>
                </Styled.ContainerText>
              </Styled.Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid item sm={12} md={6} xs={6} xl={6}>
          <Styled.ContainerDriver>
            {!addDrive ? (
              <>
                <Styled.ContainerDriverSelected>
                  <LocalShippingIcon fontSize="small" color="inherit" />
                  <Styled.ContainerText>
                    <Typography variant="caption" textAlign="start">
                      {t("deliveryman")}
                    </Typography>
                    <Typography variant="subtitle2" textAlign="start">
                      {getOrderDeliveryPersonDisplayName({
                        driver: data?.driver,
                        coverageArea: data?.coverageArea,
                      }) || t("undefined")}
                    </Typography>
                  </Styled.ContainerText>
                </Styled.ContainerDriverSelected>
                <Styled.Button
                  type="button"
                  onClick={() => {
                    setAddDrive(true);
                    handledUsers();
                  }}
                >
                  <Typography
                    variant="button"
                    fontSize="medium"
                    textAlign="start"
                  >
                    Selecionar
                  </Typography>
                </Styled.Button>
              </>
            ) : (
              <Grid container direction="row">
                <Grid item sm={12} md={media ? 8 : 12} xs={4}>
                  <Autocomplete
                    disablePortal
                    id="combo-box-demo"
                    options={IUsers || []}
                    size="small"
                    onChange={(_, e) => setDriveSelected(e as any)}
                    renderInput={(params) => (
                      <TextField {...params} label={t("selectDriver")} />
                    )}
                  />
                </Grid>
                <Grid
                  item
                  sm={12}
                  md={media ? 4 : 12}
                  xs={4}
                  display="flex"
                  flexDirection="row"
                  columnGap={2}
                  rowGap={2}
                  justifyContent="center"
                >
                  <Styled.Button
                    type="button"
                    onClick={() => setAddDrive(false)}
                  >
                    <Typography
                      variant="button"
                      fontSize="medium"
                      textAlign="start"
                    >
                      Cancelar
                    </Typography>
                  </Styled.Button>
                  <Styled.Button type="button" onClick={() => handleAddDrive()}>
                    <Typography
                      variant="button"
                      fontSize="medium"
                      textAlign="start"
                    >
                      Salvar
                    </Typography>
                  </Styled.Button>
                </Grid>
              </Grid>
            )}
          </Styled.ContainerDriver>
        </Grid>
      </Grid>
      <Styled.ContainerProducts>
        <Typography variant="subtitle2" textAlign="start" fontWeight={400}>
          {t("product")}
        </Typography>
        {data?.items?.map((product) => (
          <>
            <Styled.CardProducts>
              <Styled.ContainerProductsTitle>
                <Typography variant="subtitle2" textAlign="start">
                  {`${
                    product.quantity
                  } X ${formatByText.formatProductNameAndDescription(
                    (product as { name?: string }).name,
                    product.description,
                  )}`}
                </Typography>
                <Typography variant="subtitle2" textAlign="start">
                  {formatByText.formatCurrency(Number(product.unitPrice))}
                </Typography>
              </Styled.ContainerProductsTitle>
            </Styled.CardProducts>

            <Styled.CardProducts>
              <Styled.ContainerProductsTitle isColor={"rgba(211,49,0,0.08)"}>
                <Typography variant="subtitle2" textAlign="start">
                  {t("discounts")}
                </Typography>
                <Typography variant="subtitle2" textAlign="start">
                  {formatByText.formatCurrency(
                    data?.discount ? data?.discount : "0",
                  )}
                </Typography>
              </Styled.ContainerProductsTitle>
              <Styled.ContainerProductsTitle isColor={"rgba(211,49,0,0.08)"}>
                <Typography variant="subtitle2" textAlign="start">
                  {t("totalReceivable")}
                </Typography>
                <Typography variant="subtitle2" textAlign="start">
                  {product?.unitPrice &&
                    product?.quantity &&
                    formatByText.formatCurrency(
                      Number(
                        product.unitPrice * product.quantity - data?.discount,
                      ),
                    )}
                </Typography>
              </Styled.ContainerProductsTitle>
            </Styled.CardProducts>
          </>
        ))}
      </Styled.ContainerProducts>
      <Styled.ContainerFormOfPayment>
        <Typography variant="caption" textAlign="start">
          {t("FormOfPayment")}
        </Typography>
        <Typography
          variant="subtitle2"
          textAlign="start"
          sx={{
            paddingLeft: 2,
            background: "rgba(211,49,0,0.08)",
          }}
        >
          {data?.items?.length === 1 && data?.voucher?.valeGas
            ? "Vale Gás colaborador Supergasbras"
            : `
              ${data?.payment?.type ?? ""} (${
                data?.payment.description === "Dinheiro" &&
                data?.payment?.change > 0
                  ? `${t("money")} ${t(
                      "changeTo",
                    )} ${formatByText.formatCurrency(
                      Number(data?.payment?.change),
                    )}`
                  : data?.payment.description
              })`}
        </Typography>
      </Styled.ContainerFormOfPayment>
    </Styled.Container>
  );
};

export default CardActions;
